import React, { useState } from "react";
import { Table, Input, Select, DatePicker, Button } from "antd";
import { PlusOutlined, DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons";
import moment from "moment";

const StockEntriesTab = ({ stockEntries, onStockEntriesChange, locations = [], onOpenAddLocation }) => {
  const [locationSearchValue, setLocationSearchValue] = useState("");

  const addEntry = () => {
    onStockEntriesChange([
      ...stockEntries,
      {
        quantity: "",
        purchasePrice: "",
        transactionDate: null,
        locationId: undefined,
      },
    ]);
  };

  const removeEntry = (index) => {
    const next = stockEntries.filter((_, i) => i !== index);
    onStockEntriesChange(next);
  };

  const updateEntry = (index, field, value) => {
    const next = [...stockEntries];
    if (field === "transactionDate") {
      next[index][field] = value ? (value.format ? value.format("YYYY-MM-DD") : value) : null;
    } else {
      next[index][field] = value;
    }
    onStockEntriesChange(next);
  };

  const columns = [
    {
      title: "SL",
      key: "sl",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Opening Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 140,
      render: (val, record, index) => (
        <Input
          type="number"
          min={0}
          placeholder="Quantity"
          value={val}
          onChange={(e) =>
            updateEntry(index, "quantity", e.target.value ? parseInt(e.target.value, 10) : "")
          }
        />
      ),
    },
    {
      title: "At Price",
      dataIndex: "purchasePrice",
      key: "purchasePrice",
      width: 120,
      render: (val, record, index) => (
        <Input
          type="number"
          step="0.01"
          min={0}
          placeholder="Price"
          value={val}
          onChange={(e) =>
            updateEntry(index, "purchasePrice", e.target.value ? parseFloat(e.target.value) : "")
          }
        />
      ),
    },
    {
      title: "As of Date",
      dataIndex: "transactionDate",
      key: "transactionDate",
      width: 180,
      render: (val, record, index) => (
        <DatePicker
          style={{ width: "100%" }}
          placeholder="Select date"
          value={val ? moment(val) : null}
          onChange={(date) => updateEntry(index, "transactionDate", date)}
        />
      ),
    },
    {
      title: "Location",
      dataIndex: "locationId",
      key: "locationId",
      width: 240,
      render: (val, record, index) => {
        const locationOptions = (locations || []).map((loc) => ({
          label: loc.name,
          value: loc.id,
        }));
        const searchTrimmed = (locationSearchValue || "").trim();
        const matchesExisting = locationOptions.some(
          (opt) => opt.label?.toLowerCase() === searchTrimmed.toLowerCase()
        );
        const showAddFromHere = onOpenAddLocation && searchTrimmed && !matchesExisting;

        return (
          <Select
            style={{ width: "100%" }}
            placeholder="Search or select location"
            allowClear
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes((input || "").toLowerCase())
            }
            value={val}
            onChange={(v) => updateEntry(index, "locationId", v)}
            onSearch={(v) => setLocationSearchValue(v)}
            options={locationOptions}
            dropdownRender={(menu) => (
              <>
                {menu}
                {onOpenAddLocation && (
                  <div style={{ padding: "8px", borderTop: "1px solid #f0f0f0" }}>
                    <Button
                      type="link"
                      block
                      icon={<PlusCircleOutlined />}
                      onClick={() =>
                        onOpenAddLocation(
                          searchTrimmed || null,
                          (newLoc) => updateEntry(index, "locationId", newLoc?.id)
                        )
                      }
                    >
                      {searchTrimmed
                        ? `Add "${searchTrimmed}" from here`
                        : "Add from here"}
                    </Button>
                  </div>
                )}
              </>
            )}
          />
        );
      },
    },
    {
      title: "Delete",
      key: "delete",
      width: 80,
      render: (_, __, index) => (
        <Button
          type="primary"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => removeEntry(index)}
        />
      ),
    },
  ];

  return (
    <div>
      <Table
        dataSource={stockEntries}
        columns={columns}
        rowKey={(_, i) => i}
        pagination={false}
        scroll={{ x: 700 }}
      />
      <div style={{ marginTop: 16, textAlign: "center" }}>
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addEntry}
          size="large"
        >
          Add Stock Entry
        </Button>
      </div>
    </div>
  );
};

export default StockEntriesTab;
